<script>
  import { AxisY, AxisX } from "@urbaninstitute/dataviz-components";
  import { LayerCake, Svg, Html, raise } from "layercake";
  import { formatDate, parseDate, roundDate } from "$utils/dates.js";
  import { formatValue, formatShortValue } from "$utils/formats.js";
  import { FREQUENCIES, START_DATE } from "$utils/consts.js";
  import { max, min } from "d3-array";
  import MultiLine from "./MultiLine.svelte";
  import Voronoi from "./Voronoi.svelte";
  import ActiveAnnotation from "./ActiveAnnotation.svelte";
  import Point from "./Point.svelte";
  import { useData } from "$lib/state/data.svelte.js";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
  import { getName, getAbb } from "$utils/geoNames.js";
  import { getAllYears } from "./utils.js";

  const toolData = useData();
  const xFormat = (d) => formatDate(d, toolData.currentMetadata?.frequency);
  const tickFormat = (d) => formatDate(d, FREQUENCIES.ANNUAL);
  const yFormat = (d) => formatValue(d, toolData.currentMetadata?.units);
  const yTickFormat = (d) => formatShortValue(d, toolData.currentMetadata?.units);
  const SKIP_KEYS = ["date", "series"];
  const BG_STROKE_WIDTH = 2;
  const FOCUS_STROKE_WIDTH = 3;

  let { data, highlightGeo } = $props();

  // Check if we have valid data - now data is an array of level objects
  let hasData = $derived(data && data.length > 0 && data.some((level) => level.data?.length > 0));

  // Find the overall date range across all levels
  let dateRange = $derived.by(() => {
    if (!hasData) return { min: null, max: null };
    const startDate = START_DATE;
    return {
      min: parseDate(startDate, FREQUENCIES.INPUT),
      max: roundDate(toolData.mostRecentDate, toolData.currentMetadata?.frequency)
    };
  });
  let allYears = $derived(getAllYears(dateRange.min, dateRange.max));

  // x limit - use the max date across all levels
  let LIMIT_A = $derived(dateRange.max);

  // chart width and height
  let chartWidth = $state(375);
  let el = $state();
  const CHART_ASPECT = 16 / 9;
  let chartHeight = $derived(Math.round(chartWidth / CHART_ASPECT));

  // Get all unique geoids across all levels
  let allGeoids = $derived.by(() => {
    if (!hasData) return [];

    const geoidSet = new Set();
    for (const levelObj of data) {
      if (levelObj.data?.length > 0) {
        const keys = Object.keys(levelObj.data[0]);
        keys.forEach((key) => {
          if (!SKIP_KEYS.includes(key)) {
            geoidSet.add(key);
          }
        });
      }
    }
    return Array.from(geoidSet);
  });

  // Find the most recent value for the highlighted geo
  let selectedHighlight = $derived.by(() => {
    if (!hasData || !highlightGeo) return null;

    // Search through levels to find the most recent value for highlightGeo
    for (const levelObj of data) {
      if (levelObj.data?.length > 0) {
        // Check the last entry of this level
        const lastEntry = levelObj.data[levelObj.data.length - 1];
        if (highlightGeo in lastEntry) {
          return {
            date: lastEntry.date,
            key: highlightGeo,
            level: levelObj.level,
            value: lastEntry[highlightGeo]
          };
        }
      }
    }
    return null;
  });

  let hoverIndex = $state(null);

  // Get all values across all levels for Y extent calculation
  let allValues = $derived.by(() => {
    if (!hasData) return [];

    const values = [];
    for (const levelObj of data) {
      if (levelObj.data?.length > 0) {
        const levelGeoids = filterKeys(Object.keys(levelObj.data[0]));
        for (const entry of levelObj.data) {
          for (const geoid of levelGeoids) {
            if (entry[geoid] != null && !isNaN(entry[geoid])) {
              values.push(entry[geoid]);
            }
          }
        }
      }
    }
    return values;
  });

  let chartExtent = $derived(getYExtent(allValues));

  // Create voronoi points from all levels
  let voronoiPoints = $derived.by(() => {
    const points = [];
    for (const levelObj of data) {
      if (levelObj.data?.length > 0) {
        const levelGeoids = filterKeys(Object.keys(levelObj.data[0]));
        for (const entry of levelObj.data) {
          for (const geoid of levelGeoids) {
            if (entry[geoid] != null && !isNaN(entry[geoid])) {
              points.push({
                date: entry.date,
                value: entry[geoid],
                key: geoid,
                level: levelObj.level
              });
            }
          }
        }
      }
    }
    return points;
  });

  let hoverPoint = $derived(hoverIndex != null ? voronoiPoints[hoverIndex] : null);

  // Create a unified data structure for LayerCake
  // This merges all levels into a single array for the chart components to consume
  let chartData = $derived.by(() => {
    if (!hasData) return [];

    // Create a map to merge all dates
    const dateMap = new Map();

    for (const levelObj of data) {
      for (const entry of levelObj.data || []) {
        const dateStr = formatDate(entry.date);
        if (!dateMap.has(dateStr)) {
          dateMap.set(dateStr, { date: entry.date });
        }

        // Add all geoid values from this entry
        for (const [key, value] of Object.entries(entry)) {
          if (!SKIP_KEYS.includes(key)) {
            dateMap.get(dateStr)[key] = value;
          }
        }
      }
    }

    // Convert to array and sort by date
    return Array.from(dateMap.values()).sort((a, b) => a.date - b.date);
  });

  function isHover(geoid) {
    if (!hoverPoint) return false;
    return geoid == hoverPoint.key;
  }

  function isHighlight(geoid) {
    return geoid == highlightGeo;
  }

  function isActive(geoid) {
    return isHighlight(geoid);
  }

  let annotationData = $derived.by(() => {
    if (hoverPoint) {
      return Object.assign({}, hoverPoint, { key: getAbb(hoverPoint.key) });
    } else if (highlightGeo && selectedHighlight) {
      return Object.assign({}, selectedHighlight, { key: getAbb(selectedHighlight.key) });
    } else {
      return null;
    }
  });

  function getYExtent(vals) {
    if (!vals || vals.length === 0) return [0, null];
    // calculate range with 20% padding above and below (but not below zero)
    const maxValue = max(vals);
    const minValue = min(vals);
    if (!isNaN(minValue) && !isNaN(maxValue)) {
      const padding = (maxValue - minValue) * 0.2;
      let result = [Math.max(0, minValue - padding), maxValue + padding];
      return result;
    }
    return [0, null];
  }

  function filterKeys(keys) {
    return keys.filter((k) => !SKIP_KEYS.includes(k));
  }

  function getLineAppearance(geoid, active, hover) {
    /** @type {string} */
    let fill = urbanColors.space_gray_shade_lightest + "50";
    let dash = false;
    if (geoid === "00") {
      // national line on any chart
      fill = urbanColors.space_gray;
      dash = true;
    } else if (toolData.currentLevel == "district" && geoid.length === 2) {
      // state line on district chart
      fill = urbanColors.blue_shade_dark;
      dash = true;
    } else if (active) {
      fill = urbanColors.blue;
    } else if (hover) {
      fill = urbanColors.blue_shade_dark;
    }
    return { fill, dash };
  }

  function handleClick(d) {
    const geoid = d.key;
    if (geoid.length === 2 && toolData.currentLevel === "state") {
      toolData.currentState = geoid;
    } else if (geoid.length > 2) {
      toolData.currentState = geoid.slice(0, 2);
      toolData.currentDistrict = geoid;
    }
  }

  // Helper to get geoids for a specific level
  function getLevelGeoids(levelObj) {
    if (!levelObj?.data?.length) return [];
    return filterKeys(Object.keys(levelObj.data[0]));
  }

  $effect(() => {
    // ensure the hovered or active line moves to the top of the stack
    if (el && (hoverPoint || highlightGeo)) {
      let focusLine = el.querySelector(".line-group.active");
      if (focusLine) {
        raise(focusLine);
      }
    }
  });
</script>

{#snippet legendItem(fill, label, dash)}
  <div class="legend-item">
    <svg class="legend-line" width="20" height="16" viewBox="0 0 20 16">
      <line
        x1="0"
        y1="8"
        x2="20"
        y2="8"
        stroke={fill}
        stroke-dasharray={dash ? "4 4" : ""}
        stroke-width="2"
        stroke-linecap="round"
      ></line>
    </svg>
    <div class="legend-label">{label}</div>
  </div>
{/snippet}
<div class="legend">
  {@render legendItem(urbanColors.space_gray, getName("00"), true)}
  {#if toolData.currentLevel == "district"}
    {@render legendItem(urbanColors.blue_shade_dark, getAbb(toolData.currentState), true)}
    {@render legendItem(
      urbanColors.space_gray_shade_lightest,
      `${getAbb(toolData.currentState)} districts`,
      false
    )}
    {#if toolData.currentDistrict}
      {@render legendItem(urbanColors.blue, getAbb(toolData.currentDistrict), false)}
    {/if}
  {:else}
    {@render legendItem(urbanColors.space_gray_shade_lightest, "Other states", false)}
    {#if toolData.currentState !== "00"}
      {@render legendItem(urbanColors.blue, getAbb(toolData.currentState), false)}
    {/if}
  {/if}
</div>
<div
  class="chart-container"
  bind:clientWidth={chartWidth}
  bind:this={el}
  style:--chart-height="{chartHeight}px"
>
  <LayerCake
    data={chartData}
    x="date"
    y="Total U.S."
    padding={{ top: 10, right: 100, bottom: 20, left: 40 }}
    yDomain={chartExtent}
    xDomain={[dateRange.min, dateRange.max]}
    custom={{ xFormat, yFormat }}
  >
    <Svg>
      <AxisX formatTick={tickFormat} baseline={true} ticks={allYears} />
      <AxisY ticks={6} formatTick={yTickFormat} />

      <!-- Iterate through each level and render its lines -->
      <g class="main-lines">
        {#each data as levelObj (`${levelObj.level}-${data.currentMetric}`)}
          {@const levelGeoids = getLevelGeoids(levelObj)}
          {@const singlePoint = levelObj.data?.length === 1}
          <g class="level-group" data-level={levelObj.level}>
            {#each levelGeoids as geoid (`${geoid}-${levelObj.level}-${data.currentMetric}`)}
              {@const active = isActive(geoid)}
              {@const hover = isHover(geoid)}
              {@const { fill, dash } = getLineAppearance(geoid, active, hover)}
              <g class="line-group" class:active data-geoid={geoid}>
                <MultiLine
                  key={geoid}
                  dataSet={levelObj.data}
                  {fill}
                  {dash}
                  strokeWidth={hover || dash ? FOCUS_STROKE_WIDTH : BG_STROKE_WIDTH}
                  {hover}
                  points={hover || singlePoint}
                  missing={hover}
                  inactive={hoverPoint && !active && !hover}
                />
              </g>
            {/each}
          </g>
        {/each}
        {#if highlightGeo}
          <g class="highlight-line">
            <MultiLine
              key={highlightGeo}
              dataSet={data?.[0]?.data || []}
              fill={urbanColors.blue}
              strokeWidth={FOCUS_STROKE_WIDTH}
              points={false}
              missing={true}
              hover
            />
          </g>
        {/if}
      </g>

      {#if hoverPoint}
        <Point
          data={hoverPoint}
          fill={getLineAppearance(hoverPoint.key, isHighlight(hoverPoint.key), true).fill}
        />
      {:else if highlightGeo && selectedHighlight}
        <Point
          data={selectedHighlight}
          fill={getLineAppearance(selectedHighlight.key, true).fill}
        />
      {/if}

      <g class="voronoi">
        <Voronoi
          points={voronoiPoints}
          bind:activeIndex={hoverIndex}
          onclick={(d) => handleClick(d)}
        />
      </g>
    </Svg>
    <Html pointerEvents={false}>
      {#if annotationData}
        <ActiveAnnotation
          data={annotationData}
          dateFormat={(d) => {
            const meta = toolData.getMetadata(annotationData.level, toolData.currentMetric);
            return formatDate(d, meta?.frequency);
          }}
        />
      {/if}
    </Html>
  </LayerCake>
</div>

<style>
  .chart-container {
    width: 100%;
    height: var(--chart-height, 250px);
  }
  .controls .control-label {
    margin-bottom: 0.5em !important;
  }

  :global(.y-axis .tick:first-of-type line.gridline) {
    stroke: var(--color-gray-shade-light) !important;
  }
  .legend {
    display: flex;
    margin-block: var(--spacing-4);
    flex-wrap: wrap;
  }
  .legend-item {
    display: flex;
    align-items: center;
    margin-right: 1em;
    gap: var(--spacing-2);
  }
  .legend-line {
    flex-grow: 0;
  }
</style>
